import { relativeLuminance, strongWeight, type WidgetTokens } from "../theme";

/**
 * 表单件（输入框、按钮、可关闭提示）共用的样式。宿主要把它放进同一个 Shadow DOM，
 * 颜色从组件根节点上的 --gn-form-* 变量读，跟着商家主题走。
 */
export const FORM_STYLES = `
.gn-form-field { display:flex; flex-direction:column; gap:6px; min-width:0; }
.gn-form-label { font-size:12px; color:var(--gn-form-quiet); font-weight:var(--gn-form-strong); }
.gn-form-label-note { font-weight:400; color:var(--gn-form-faint); }
.gn-form-wrap { box-sizing:border-box; display:flex; align-items:stretch; height:44px; overflow:hidden; border:0; border-radius:var(--gn-form-r-input); background:var(--gn-form-sheet); transition:background .15s, box-shadow .15s; }
.gn-form-wrap:focus-within { background:var(--gn-form-sheet); box-shadow:0 0 0 2px var(--gn-form-ring); }
.gn-form-wrap[data-invalid] { box-shadow:0 0 0 1.5px var(--gn-form-danger); }
.gn-form-wrap[data-disabled] { opacity:.55; }
.gn-form-prefix { display:flex; align-items:center; padding:0 10px 0 14px; border-right:1px solid var(--gn-form-dash); color:var(--gn-form-quiet); font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:14px; font-weight:500; }
.gn-form-input { flex:1 1 0; min-width:0; padding:0 14px; border:0; outline:none; background:transparent; color:var(--gn-form-ink); font:inherit; font-size:14px; }
.gn-form-input[data-mono] { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; letter-spacing:.06em; font-variant-numeric:tabular-nums; }
.gn-form-input::placeholder { color:var(--gn-form-faint); }
.gn-form-input:disabled { cursor:not-allowed; }
.gn-form-hint { margin:0; font-size:12px; line-height:1.4; color:var(--gn-form-quiet); }
.gn-form-error { display:flex; gap:6px; align-items:flex-start; margin:0; font-size:12px; line-height:1.4; color:var(--gn-form-danger); }
.gn-form-error > svg { flex:none; }
.gn-btn { box-sizing:border-box; display:inline-flex; align-items:center; justify-content:center; gap:6px; min-height:40px; padding:0 16px; border:0; border-radius:var(--gn-form-r-pill); font:inherit; font-size:13px; font-weight:var(--gn-form-strong); letter-spacing:-.005em; line-height:1.2; text-align:center; cursor:pointer; transition:background .15s, color .15s, box-shadow .15s, filter .15s; }
.gn-btn { text-decoration:none; white-space:nowrap; }
.gn-btn[data-full] { width:100%; }
.gn-btn[data-compact] { min-height:32px; padding:0 12px; font-size:12px; }
.gn-btn:hover:not([data-disabled]) { filter:brightness(.96); }
.gn-btn[data-variant="primary"] { background:var(--gn-form-answer); color:var(--gn-form-answer-fg); }
.gn-btn[data-variant="secondary"] { background:transparent; color:var(--gn-form-ink); box-shadow:inset 0 0 0 1.5px var(--gn-form-ink); }
.gn-btn[data-variant="secondary"]:hover:not([data-disabled]) { background:var(--gn-form-chip); filter:none; }
.gn-btn[data-variant="ghost"] { background:transparent; color:var(--gn-form-quiet); padding:0 12px; }
.gn-btn:focus-visible { outline:2px solid var(--gn-form-ring); outline-offset:2px; }
.gn-btn[data-disabled] { cursor:not-allowed; }
.gn-btn[data-variant="primary"][data-disabled], .gn-btn[data-variant="secondary"][data-disabled] { background:var(--gn-form-chip); color:var(--gn-form-quiet); box-shadow:none; }
.gn-btn[data-variant="ghost"][data-disabled] { color:var(--gn-form-quiet); }
.gn-toast-viewport { position:absolute; z-index:2; top:0; left:0; right:0; display:flex; flex-direction:column; gap:8px; pointer-events:none; }
.gn-toast { box-sizing:border-box; pointer-events:auto; display:flex; align-items:flex-start; gap:8px; padding:10px 8px 10px 14px; border:0; border-radius:var(--gn-form-r-well); background:var(--gn-form-sheet); color:var(--gn-form-quiet); font-size:12px; line-height:1.4; box-shadow:0 0 0 1px var(--gn-form-line), var(--gn-form-shadow); transition:opacity .2s, transform .2s; }
.gn-toast[data-tone="danger"] { background:color-mix(in srgb, var(--gn-form-danger) 8%, var(--gn-form-sheet)); }
.gn-toast[data-tone="danger"] { color:var(--gn-form-ink); }
.gn-toast[data-tone="danger"] .gn-toast-title { color:var(--gn-form-danger); }
.gn-toast[data-starting-style], .gn-toast[data-ending-style] { opacity:0; transform:translateY(-6px); }
.gn-toast-body { flex:1 1 0; min-width:0; display:flex; flex-direction:column; gap:1px; }
.gn-toast-title { margin:0; font-size:inherit; line-height:inherit; color:var(--gn-form-ink); font-weight:var(--gn-form-strong); }
.gn-toast-body p { margin:0; }
.gn-toast-close { flex:none; display:inline-flex; align-items:center; justify-content:center; width:24px; height:24px; margin:-2px 0; padding:0; border:0; border-radius:var(--gn-form-r-pill); background:transparent; color:var(--gn-form-faint); cursor:pointer; }
.gn-toast-close:hover { background:var(--gn-form-chip); color:var(--gn-form-ink); }
.gn-toast-close:focus-visible { outline:2px solid var(--gn-form-ring); }
@media (prefers-reduced-motion: reduce) { .gn-toast, .gn-btn, .gn-form-wrap { transition:none; } }
`;

/** 压在墨黑实心块上的字色：墨色是浅色（深色面板）就用近黑，否则用白。变量或非 6 位十六进制的墨色（后台回放）回落到卡片底。 */
function onInk(t: WidgetTokens): string {
  if (!/^#[0-9a-f]{6}$/i.test(t.ink)) return t.surfaceBg;
  return relativeLuminance(t.ink) > 0.5 ? "#1C1B1A" : "#FFFFFF";
}

/** 深色面板上的阴影要更重；判断依据同 onInk。 */
function isDarkInk(t: WidgetTokens): boolean {
  return /^#[0-9a-f]{6}$/i.test(t.ink) && relativeLuminance(t.ink) > 0.5;
}

/**
 * 组件根节点上的 --gn-form-* 变量，全部取自主题 token。
 * 软胶囊皮肤：主操作和「当前项」是墨色实心块（--gn-form-answer），商家主色只留给小圆点（--gn-form-dot）；
 * 圆角按控件圆角放大，商家把圆角调到 0 时整套皮肤跟着变方。
 *
 * 分层靠底色深浅，不靠阴影（白卡加阴影落在近白的面板上几乎分不出来）：
 * 卡片是一块托盘（--gn-form-tray，取主题的 trayBg：浅色实底面板上和 AI 气泡同色，其余情况比面板深一档，深色面板上则亮一档）；
 * 要读的内容放在托盘上的纸面里（--gn-form-sheet：输入框、尺码表、对比表、物流块）；纸面里的小胶囊
 * 用 --gn-form-chip。次要按钮是墨线描边的空心胶囊，和实心墨黑的主按钮、白色的输入框都分得开。
 * 卡片里的次要文字用 --gn-form-quiet（半透明墨色，托盘和纸面上都到 4.5:1）。
 *
 * --gn-form-fill / -fill-fg / -focus / -focus-soft / -link / -radius / -card-radius、-panel / -subtle / -bg / -muted
 * 保持原来的语义，空间画板等别的组件还在读它们；软胶囊皮肤自己读 -answer、-ring、-r-*、-tray、-sheet、-chip、-quiet。
 */
export function formVars(t: WidgetTokens): Record<string, string | number> {
  const dark = isDarkInk(t);
  return {
    "--gn-form-answer": t.ink,
    "--gn-form-answer-fg": onInk(t),
    "--gn-form-bg": t.surfaceBg,
    "--gn-form-card-radius": t.cardRadius,
    "--gn-form-danger": t.dangerText,
    "--gn-form-chip": `color-mix(in srgb, ${t.ink} ${dark ? "10%" : "7%"}, transparent)`,
    "--gn-form-dash": `color-mix(in srgb, ${t.ink} 24%, transparent)`,
    "--gn-form-divider": t.line,
    "--gn-form-dot": t.accentFill,
    "--gn-form-faint": t.faint,
    "--gn-form-fill": t.accentFill,
    "--gn-form-fill-fg": t.accentFillFg,
    "--gn-form-focus": t.accentFill,
    "--gn-form-focus-soft": t.accentSoft,
    "--gn-form-ink": t.ink,
    "--gn-form-line": t.line,
    "--gn-form-link": t.accentText,
    "--gn-form-muted": t.muted,
    "--gn-form-panel": t.surfaceBg,
    "--gn-form-raised": dark ? "#34343E" : t.surfaceBg,
    "--gn-form-quiet": `color-mix(in srgb, ${t.ink} 64%, transparent)`,
    "--gn-form-r-card": `min(28px, calc(${t.ctrlRadius} * 2.2))`,
    "--gn-form-r-cell": `min(18px, calc(${t.ctrlRadius} * 1.4))`,
    "--gn-form-r-input": `min(18px, calc(${t.ctrlRadius} * 1.4))`,
    "--gn-form-r-pill": t.chipRadius,
    "--gn-form-r-tile": `min(24px, calc(${t.ctrlRadius} * 2))`,
    "--gn-form-r-well": `min(20px, calc(${t.ctrlRadius} * 1.6))`,
    "--gn-form-radius": `min(${t.ctrlRadius}, 22px)`,
    "--gn-form-ring": t.ink,
    "--gn-form-ring-soft": `color-mix(in srgb, ${t.ink} 14%, transparent)`,
    "--gn-form-shadow": dark
      ? "0 1px 2px rgba(0,0,0,.2), 0 10px 28px rgba(0,0,0,.25)"
      : "0 1px 2px rgba(28,27,26,.05), 0 10px 28px rgba(28,27,26,.07)",
    "--gn-form-shadow-pill": dark
      ? "0 1px 2px rgba(0,0,0,.25)"
      : "0 1px 2px rgba(28,27,26,.06)",
    "--gn-form-sheet": dark
      ? `color-mix(in srgb, ${t.ink} 8%, transparent)`
      : "#FFFFFF",
    "--gn-form-strong": strongWeight(t),
    "--gn-form-subtle": t.subtle,
    "--gn-form-tray": t.trayBg,
  };
}
